import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Zap, ShieldCheck, TrendingUp } from "lucide-react";
import { StartBuild, ScrollCTA } from "@/components/site/primitives";
import { Integrations } from "@/components/site/Integrations";
import { Seo } from "@/seo/Seo";
import { organizationSchema, websiteSchema } from "@/seo/config";

const ease = [0.22, 1, 0.36, 1];
const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-90px" },
  transition: { duration: 0.85, ease },
};
const CONTAINER = "max-w-[1560px] mx-auto px-6 md:px-10 lg:px-16";

/* Photorealistic product shot, edge-masked so it floats on the black canvas */
const SHOT_MASK = "[mask-image:radial-gradient(ellipse_86%_90%_at_50%_50%,#000_60%,transparent_100%)]";
function Shot({ src, alt, portrait = false }) {
  return (
    <motion.div {...reveal} className="relative flex items-center justify-center">
      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[62%] w-[74%] rounded-full bg-cyan-500/[0.06] blur-[120px]" />
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`relative w-full h-auto object-contain ${portrait ? "max-w-[300px]" : "max-w-full"} ${SHOT_MASK}`}
      />
    </motion.div>
  );
}

/* ============================= HERO ============================= */
function Hero() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -70]);
  const yDevices = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, reduce ? 1 : 0]);

  const container = { hidden: {}, show: { transition: { staggerChildren: 0.14, delayChildren: 0.15 } } };
  const device = {
    hidden: { opacity: 0, y: 40, scale: 0.97 },
    show: { opacity: 1, y: 0, scale: 1, transition: { duration: 1, ease } },
  };
  const line = {
    hidden: { opacity: 0, y: 28 },
    show: { opacity: 1, y: 0, transition: { duration: 0.8, ease } },
  };

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden pt-28 md:pt-24 pb-16">
      <div className="pointer-events-none absolute -top-24 right-[8%] h-[560px] w-[560px] rounded-full bg-cyan-500/[0.06] blur-[150px]" />

      <div className={`relative w-full ${CONTAINER} grid lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.16fr)] gap-14 lg:gap-8 items-center`}>
        {/* LEFT */}
        <motion.div variants={container} initial="hidden" animate="show" style={{ y: yText, opacity: fade }} className="relative z-10">
          <motion.div variants={line} className="mono-label text-cyan-400 mb-7">Digital Product Studio</motion.div>
          <motion.h1 variants={line} className="font-heading font-extrabold uppercase tracking-[-0.03em] leading-[0.84] text-[clamp(3.1rem,7.2vw,7.2rem)]">
            You have<br />the idea.<br />We will<br />
            <span className="text-cyan-400">Build it.</span>
          </motion.h1>
          <motion.p variants={line} className="mt-8 max-w-md text-lg text-zinc-400 leading-relaxed">
            Modern AI makes building more accessible. Experience makes building it right possible.
          </motion.p>
          <motion.div variants={line} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-7">
            <StartBuild testid="hero-start-build-btn" size="xl" label="Start Your Product Journey" className="justify-center" />
            <ScrollCTA target="capabilities" testid="hero-see-what-we-build-btn">See What We Build</ScrollCTA>
          </motion.div>
        </motion.div>

        {/* RIGHT — photorealistic product render */}
        <motion.div variants={container} initial="hidden" animate="show" style={{ y: yDevices }}
          className="relative w-full flex items-center justify-center">
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[70%] w-[80%] rounded-full bg-cyan-500/[0.10] blur-[140px]" />
          <motion.img
            variants={device}
            src="/hero-devices.jpg"
            alt="AIROTECH product interfaces shown on a smartphone and a laptop"
            fetchPriority="high"
            className="relative w-full max-w-[820px] h-auto object-contain [mask-image:radial-gradient(ellipse_82%_84%_at_50%_48%,#000_56%,transparent_100%)]"
          />
        </motion.div>
      </div>
    </section>
  );
}

/* ===================== EDITORIAL STATEMENT (quiet) ===================== */
function Statement() {
  return (
    <section className="py-28 md:py-44 border-t border-[#131313]">
      <div className={CONTAINER}>
        <motion.p {...reveal} className="mono-label text-zinc-600 mb-10">The AIROTECH Approach</motion.p>
        <motion.h2 {...reveal} className="font-serif-ed text-white text-4xl md:text-6xl lg:text-[4.6rem] leading-[1.06] max-w-5xl">
          We take an idea and turn it into a product{" "}
          <span className="text-zinc-500">people actually use</span>. From first concept to a live, scalable build.
        </motion.h2>
      </div>
    </section>
  );
}

/* ===================== CAPABILITIES (alternating panels) ===================== */
function CapabilityPanel({ index, kicker, title, desc, children, reverse }) {
  return (
    <motion.div {...reveal} className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
      <div className={reverse ? "lg:order-2" : ""}>
        <div className="flex items-baseline gap-4 mb-7">
          <span className="font-serif-ed text-cyan-400/70 text-3xl leading-none">{index}</span>
          <span className="mono-label text-zinc-500">{kicker}</span>
        </div>
        <h3 className="font-heading text-4xl md:text-5xl text-white tracking-tight leading-[1.02]">{title}</h3>
        <p className="mt-6 max-w-md text-lg text-zinc-400 leading-relaxed">{desc}</p>
      </div>
      <div className={`relative ${reverse ? "lg:order-1" : ""}`}>
        <div className="pointer-events-none absolute inset-0 -z-10 mx-auto max-w-[72%] rounded-full bg-cyan-500/[0.035] blur-[120px]" />
        {children}
      </div>
    </motion.div>
  );
}

function Capabilities() {
  return (
    <section id="capabilities" className="py-24 md:py-36 border-t border-[#131313]">
      <div className={CONTAINER}>
        <motion.p {...reveal} className="mono-label text-zinc-600 mb-5">What We Build</motion.p>
        <motion.h2 {...reveal} className="font-heading text-white text-4xl md:text-6xl tracking-tight max-w-3xl leading-[1.02]">
          Digital products designed to move from idea to market.
        </motion.h2>

        <div className="mt-24 space-y-28 md:space-y-40">
          <CapabilityPanel index="01" kicker="Web" title="Websites & web applications" desc="Fast, responsive marketing sites and full web applications with real data, dashboards and workflows — engineered to convert and to scale.">
            <Shot src="/mockups/web.jpg" alt="Modern marketing website shown on a laptop" />
          </CapabilityPanel>
          <CapabilityPanel index="02" kicker="Mobile" title="Native-feeling mobile apps" desc="iOS and Android experiences with fluid navigation, offline states and polished detail — products that feel at home on the device." reverse>
            <Shot src="/mockups/mobile.jpg" alt="Mobile app shown on a smartphone" portrait />
          </CapabilityPanel>
          <CapabilityPanel index="03" kicker="PWA" title="Progressive web apps" desc="Installable, offline-ready web apps that sync in the background and work anywhere — no app store required.">
            <Shot src="/mockups/pwa.jpg" alt="Progressive web app shown on a smartphone" portrait />
          </CapabilityPanel>
          <CapabilityPanel index="04" kicker="SaaS" title="SaaS platforms & dashboards" desc="Multi-tenant platforms with billing, analytics and role-based access — the operational core products your customers log into every day." reverse>
            <Shot src="/mockups/saas.jpg" alt="SaaS analytics dashboard shown on a desktop monitor" />
          </CapabilityPanel>
          <CapabilityPanel index="05" kicker="AI" title="AI products & workflows" desc="Assistants and automations that classify, route and resolve — AI woven into real product surfaces, not bolted on as a gimmick.">
            <Shot src="/mockups/ai.jpg" alt="AI product interface shown on a desktop monitor" />
          </CapabilityPanel>
          <CapabilityPanel index="06" kicker="Custom" title="Custom software" desc="Bespoke internal tools and systems built around how your business actually operates — architected to last." reverse>
            <Shot src="/mockups/custom.jpg" alt="Custom internal software shown on a desktop monitor" />
          </CapabilityPanel>
        </div>
      </div>
    </section>
  );
}

/* ===================== JOURNEY (editorial typographic) ===================== */
const STAGES = [
  ["01", "Idea", "It begins with a short, guided conversation about the product you want to exist — no technical knowledge required."],
  ["02", "Strategy", "We shape scope, complexity and the right build level into a clear, honest plan you can act on."],
  ["03", "Design", "High-fidelity interfaces that look and feel like the finished product, long before a line ships."],
  ["04", "Build", "Real engineering — architecture, data and security built to survive contact with real users."],
  ["05", "Launch", "We ship to web, mobile and the app stores, and make sure it holds up on day one."],
  ["06", "Grow", "We measure, iterate and scale — with ongoing support when you want a team in your corner."],
];
function Journey() {
  return (
    <section className="py-24 md:py-40 border-t border-[#131313]">
      <div className={CONTAINER}>
        <motion.p {...reveal} className="mono-label text-zinc-600 mb-6">The Journey</motion.p>
        <motion.h2 {...reveal} className="font-serif-ed italic text-white text-5xl md:text-7xl lg:text-8xl leading-[0.95]">
          From idea to product.
        </motion.h2>

        <div className="mt-20 border-t border-[#161616]">
          {STAGES.map(([n, name, copy]) => (
            <motion.div key={n} {...reveal}
              className="group grid md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-6 md:gap-16 py-10 md:py-16 border-b border-[#161616] items-baseline">
              <div className="flex items-baseline gap-6">
                <span className="font-serif-ed text-[#262626] text-5xl md:text-7xl leading-none transition-colors duration-500 group-hover:text-cyan-400/50">{n}</span>
                <h3 className="font-serif-ed italic text-white text-4xl md:text-6xl leading-none">{name}</h3>
              </div>
              <p className="text-zinc-400 text-lg md:text-xl leading-relaxed max-w-2xl md:pt-3">{copy}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===================== SELECTED WORK (large case studies) ===================== */
const WORK = [
  { cat: "SaaS Platform", name: "Northwind Analytics", desc: "A subscription analytics platform — revenue, churn and cohort insight in one operational dashboard.", src: "/mockups/saas.jpg" },
  { cat: "Mobile Product", name: "Pulse Activity", desc: "A fitness and activity app with goals, streaks and progress that keeps people coming back.", src: "/mockups/mobile.jpg", portrait: true, reverse: true },
  { cat: "AI Product", name: "Atlas Assistant", desc: "An AI support product that classifies, routes and resolves requests in under half a second.", src: "/mockups/ai.jpg" },
];
function SelectedWork() {
  return (
    <section className="py-24 md:py-36 border-t border-[#131313]">
      <div className={CONTAINER}>
        <motion.p {...reveal} className="mono-label text-zinc-600 mb-5">Selected Work</motion.p>
        <motion.h2 {...reveal} className="font-serif-ed italic text-white text-5xl md:text-7xl leading-[0.95]">Built to last.</motion.h2>

        <div className="mt-24 space-y-28 md:space-y-40">
          {WORK.map((w) => (
            <motion.div key={w.name} {...reveal} className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
              <div className={w.reverse ? "lg:order-2" : ""}>
                <div className="mono-label text-zinc-500 mb-5">{w.cat}</div>
                <h3 className="font-serif-ed text-white text-4xl md:text-6xl leading-[1.0]">{w.name}</h3>
                <p className="mt-6 max-w-md text-lg text-zinc-400 leading-relaxed">{w.desc}</p>
              </div>
              <div className={w.reverse ? "lg:order-1" : ""}>
                <Shot src={w.src} alt={`${w.name} product interface`} portrait={w.portrait} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===================== PRINCIPLES (trust) ===================== */
const VALUES = [
  { icon: Zap, title: "Fast delivery", copy: "Focused scope and experienced operators mean momentum from day one — not endless discovery." },
  { icon: ShieldCheck, title: "Secure & reliable", copy: "Real architecture, data and security practices, so your product survives the real world." },
  { icon: TrendingUp, title: "Built to scale", copy: "We build for where you're going, with room to grow features, users and revenue." },
];
function Principles() {
  return (
    <section className="py-24 md:py-32 border-t border-[#131313]">
      <div className={CONTAINER}>
        <div className="grid md:grid-cols-3 md:divide-x divide-[#161616]">
          {VALUES.map((v) => (
            <motion.div key={v.title} {...reveal} className="md:px-12 first:md:pl-0 last:md:pr-0 py-4">
              <v.icon className="h-5 w-5 text-cyan-400/80" />
              <h3 className="mt-7 font-heading text-2xl text-white tracking-tight">{v.title}</h3>
              <p className="mt-4 text-zinc-400 leading-relaxed">{v.copy}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===================== FINAL CTA ===================== */
function FinalCTA() {
  return (
    <section className="relative overflow-hidden py-32 md:py-48 border-t border-[#131313]">
      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[760px] rounded-full bg-cyan-500/[0.06] blur-[150px]" />
      <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
        <motion.h2 {...reveal} className="font-serif-ed text-white text-5xl md:text-7xl lg:text-[5.2rem] leading-[1.02]">
          Your idea doesn't need<br />to stay an idea.
        </motion.h2>
        <motion.p {...reveal} className="mt-8 max-w-xl mx-auto text-lg text-zinc-400 leading-relaxed">
          Begin a guided product journey. Tell us what you want to build — we'll take it from there.
        </motion.p>
        <motion.div {...reveal} className="mt-12 flex justify-center">
          <StartBuild testid="closing-start-build-btn" size="xl" label="Start Your Product Journey" />
        </motion.div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div>
      <Seo path="/" schema={[organizationSchema(), websiteSchema()]} />
      <Hero />
      <Statement />
      <Integrations />
      <Capabilities />
      <Journey />
      <SelectedWork />
      <Principles />
      <FinalCTA />
    </div>
  );
}
